/* specs/04-product-card.md. Event-delegated so it keeps working after any
   section re-renders a card (drawer upsell rail, collection load-more, etc). */

/* Shared wishlist store, used here (the heart toggle) and on the account
   page (rendering saved products). Guest and logged-in alike: localStorage
   only, keyed per browser — there's no backend to sync across devices
   without a wishlist app or an app-proxy, so this is a real, documented
   limit rather than a true per-customer wishlist. Entries store both id
   and handle so the account page can fetch full product data from the
   public /products/{handle}.js endpoint, with no API token needed. */
window.VelourWishlist = {
  key: 'velour_wishlist',

  getAll() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.key) || '[]');
      // Back-compat: earlier builds stored bare id strings.
      return raw.map((entry) => (typeof entry === 'string' ? { id: entry, handle: null } : entry));
    } catch (e) {
      return [];
    }
  },

  has(id) {
    return this.getAll().some((entry) => entry.id === id);
  },

  add(id, handle) {
    const all = this.getAll().filter((entry) => entry.id !== id);
    all.push({ id, handle });
    localStorage.setItem(this.key, JSON.stringify(all));
  },

  remove(id) {
    const all = this.getAll().filter((entry) => entry.id !== id);
    localStorage.setItem(this.key, JSON.stringify(all));
  },
};

function velourFormatMoney(cents) {
  return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
}

function velourPackSizeSelect(chip) {
  const group = chip.closest('[data-pack-size]');
  const card = chip.closest('[data-product-card]');
  if (!group || !card) return;

  group.querySelectorAll('.chip').forEach((c) => {
    c.classList.remove('is-selected');
    c.setAttribute('aria-pressed', 'false');
  });
  chip.classList.add('is-selected');
  chip.setAttribute('aria-pressed', 'true');

  const price = parseInt(chip.dataset.price, 10);
  const compareAt = parseInt(chip.dataset.compareAt, 10) || 0;
  const units = parseInt(chip.dataset.units, 10) || 1;
  const variantId = chip.dataset.variantId;
  const isSoldOut = chip.classList.contains('is-sold-out');

  // Base per-unit reference: the first in-stock pack, as on the prototype.
  const chips = Array.from(group.querySelectorAll('.chip'));
  const baseChip = chips.find((c) => !c.classList.contains('is-sold-out')) || chips[0];
  const basePerUnit = parseInt(baseChip.dataset.price, 10) / (parseInt(baseChip.dataset.units, 10) || 1);

  const saveTotal = Math.round((basePerUnit * units - price) / 100) * 100;

  const priceEl = card.querySelector('.product-card__price .price-row__current');
  const compareEl = card.querySelector('.product-card__price .price-row__compare');
  if (priceEl) priceEl.textContent = velourFormatMoney(price);
  if (compareEl) {
    if (compareAt > price) {
      compareEl.textContent = velourFormatMoney(compareAt);
      compareEl.hidden = false;
    } else {
      compareEl.hidden = true;
    }
  }

  const noteEl = card.querySelector('[data-pack-note]');
  if (noteEl) {
    if (isSoldOut) {
      noteEl.textContent = 'This pack is sold out';
      noteEl.hidden = false;
    } else if (units > 1) {
      const each = Math.round(price / 100 / units) * 100;
      noteEl.textContent = velourFormatMoney(each) + ' each' + (saveTotal > 0 ? ' · save ' + velourFormatMoney(saveTotal) : '');
      noteEl.hidden = false;
    } else {
      noteEl.hidden = true;
    }
  }

  const quickAdd = card.querySelector('[data-quick-add]');
  if (quickAdd) {
    quickAdd.dataset.variantId = variantId;
    quickAdd.disabled = isSoldOut;
    quickAdd.dataset.available = String(!isSoldOut);
    const label = quickAdd.querySelector('[data-quick-add-label]');
    if (label) label.textContent = isSoldOut ? 'Notify me' : 'Add to bag';
  }
}

function velourQuickAdd(button) {
  if (button.dataset.available === 'false') {
    // Generic back-in-stock hook: swap the copy and mark it locally.
    // Wire the real subscription call here once a back-in-stock app is chosen.
    button.disabled = true;
    const label = button.querySelector('[data-quick-add-label]');
    if (label) label.textContent = "We'll WhatsApp you";
    return;
  }

  const variantId = button.dataset.variantId;
  if (!variantId) return;
  const silent = button.hasAttribute('data-silent-add');
  const label = button.querySelector('[data-quick-add-label]');
  const originalLabel = label ? label.textContent : '';
  button.disabled = true;
  if (label) label.textContent = 'Adding…';

  window.VelourCart.addItem(variantId, 1, { openDrawer: !silent })
    .then(() => {
      button.disabled = false;
      if (label) label.textContent = 'Add another';
    })
    .catch((error) => {
      console.error(error);
      button.disabled = false;
      if (label) label.textContent = originalLabel;
    });
}

function velourWishlistToggle(button) {
  const productId = button.dataset.productId;
  const handle = button.dataset.productHandle;
  const pressed = window.VelourWishlist.has(productId);
  if (pressed) {
    window.VelourWishlist.remove(productId);
  } else {
    window.VelourWishlist.add(productId, handle);
  }
  button.setAttribute('aria-pressed', String(!pressed));
  button.textContent = !pressed ? '♥' : '♡';
}

document.addEventListener('click', (event) => {
  const chip = event.target.closest('[data-pack-size] .chip');
  if (chip) {
    velourPackSizeSelect(chip);
    return;
  }
  const quickAdd = event.target.closest('[data-quick-add]');
  if (quickAdd) {
    velourQuickAdd(quickAdd);
    return;
  }
  const wishlist = event.target.closest('[data-wishlist-toggle]');
  if (wishlist) {
    velourWishlistToggle(wishlist);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    if (window.VelourWishlist.has(button.dataset.productId)) {
      button.setAttribute('aria-pressed', 'true');
      button.textContent = '♥';
    }
  });
});

document.addEventListener('velour:cart:updated', (event) => {
  const variantIds = event.detail.variantIds || [];
  document.querySelectorAll('[data-quick-add]').forEach((button) => {
    if (button.dataset.available === 'false') return;
    const label = button.querySelector('[data-quick-add-label]');
    if (!label) return;
    label.textContent = variantIds.includes(button.dataset.variantId) ? 'Add another' : 'Add to bag';
  });
});
